import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layers, Plus, Edit, Trash2, ArrowLeft, MoreVertical,
  Box, Zap, Wallet, Wrench, Check, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeIcons = {
  "Physical": Box,
  "Digital": Zap,
  "Float/Balance": Wallet,
  "Service": Wrench
};

const typeColors = {
  "Physical": "bg-blue-100 text-blue-800",
  "Digital": "bg-purple-100 text-purple-800",
  "Float/Balance": "bg-amber-100 text-amber-800",
  "Service": "bg-emerald-100 text-emerald-800"
};

export default function Classifications() {
  const navigate = useNavigate();
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [form, setForm] = useState({ name: "", type: "Physical" });

  useEffect(() => {
    loadClassifications();
  }, []);

  const loadClassifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory/classifications`);
      setClassifications(response.data);
    } catch (error) {
      console.error("Failed to load classifications:", error);
      toast.error("Failed to load classifications");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      if (editingClass) {
        await axios.put(`${API}/inventory/classifications/${editingClass.id}`, form);
        toast.success("Classification updated");
      } else {
        await axios.post(`${API}/inventory/classifications`, form);
        toast.success("Classification created");
      }
      setShowDialog(false);
      resetForm();
      loadClassifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save classification");
    }
  };

  const handleDelete = async (classification) => {
    if (!confirm(`Delete "${classification.name}"? This cannot be undone if items are not linked.`)) return;
    
    try {
      await axios.delete(`${API}/inventory/classifications/${classification.id}`);
      toast.success("Classification deleted");
      loadClassifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete classification");
    }
  };

  const resetForm = () => {
    setEditingClass(null);
    setForm({ name: "", type: "Physical" });
  };

  const openEdit = (classification) => {
    setEditingClass(classification);
    setForm({ name: classification.name, type: classification.type });
    setShowDialog(true);
  };

  const getTypeIcon = (type) => {
    const Icon = typeIcons[type] || Box;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6" data-testid="classifications-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/inventory")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Inventory
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers className="w-7 h-7 text-primary" />
            Classifications
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Organize inventory items into categories
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-classification-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Classification
        </Button>
      </div>

      {/* Classifications Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                  Loading...
                </TableCell>
              </TableRow>
            ) : classifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                  No classifications yet. Create your first one!
                </TableCell>
              </TableRow>
            ) : (
              classifications.map((classification) => (
                <TableRow key={classification.id} data-testid={`class-row-${classification.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getTypeIcon(classification.type)}
                      <span className="font-medium">{classification.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={typeColors[classification.type] || "bg-gray-100"}>
                      {classification.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {classification.active ? (
                      <Badge variant="success" className="bg-emerald-100 text-emerald-800">
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <X className="w-3 h-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(classification)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(classification)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Suggested Classifications */}
      {classifications.length === 0 && !loading && (
        <div className="bg-gray-50 rounded-lg border border-dashed p-6">
          <h3 className="font-medium text-gray-700 mb-3">Suggested Classifications</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { name: "Airtime Cards", type: "Digital" },
              { name: "SIM Cards", type: "Physical" },
              { name: "Mobile Money Float", type: "Float/Balance" },
              { name: "Accessories", type: "Physical" },
              { name: "Stationery", type: "Physical" },
              { name: "Services", type: "Service" }
            ].map((suggestion) => (
              <Button
                key={suggestion.name}
                variant="outline"
                size="sm"
                onClick={() => {
                  setForm(suggestion);
                  setShowDialog(true);
                }}
                className="text-sm"
              >
                <Plus className="w-3 h-3 mr-1" />
                {suggestion.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="classification-dialog">
          <DialogHeader>
            <DialogTitle>{editingClass ? "Edit Classification" : "Add Classification"}</DialogTitle>
            <DialogDescription>
              {editingClass ? "Update classification details" : "Create a new category for your inventory items"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Electronics, Stationery, Services"
                data-testid="class-name-input"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger data-testid="class-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Physical">
                    <span className="flex items-center gap-2">
                      <Box className="w-4 h-4" />
                      Physical Goods
                    </span>
                  </SelectItem>
                  <SelectItem value="Digital">
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Digital Products
                    </span>
                  </SelectItem>
                  <SelectItem value="Float/Balance">
                    <span className="flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      Float / Balance
                    </span>
                  </SelectItem>
                  <SelectItem value="Service">
                    <span className="flex items-center gap-2">
                      <Wrench className="w-4 h-4" />
                      Service
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {form.type === "Physical" && "Tangible items that can be stored and shipped"}
                {form.type === "Digital" && "Virtual products like airtime, software licenses"}
                {form.type === "Float/Balance" && "Mobile money float, prepaid balances"}
                {form.type === "Service" && "Non-inventory services you provide"}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-classification-btn">
              {editingClass ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
